/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {InviteGenericStep, InviteStepParams, InviteStepProps} from './_InviteGenericStep';
import {SimpleTabs, ITab} from '@techsee/techsee-ui-common/lib/tabs/simple';
import {AgentGuidance} from '../components/agent-guidance/AgentGuidance';
import {InviteStepsLayout} from './_Shared';
import {IInviteFlowManager, InviteDeviceType, InviteStepsEnum} from '../../_contracts/InviteContracts';
import {IMainLayoutController} from '../../../../layouts/main-layout/layout.controller';
import {IDomUtilsService} from '@techsee/techsee-client-infra/lib/services/DomUtilsService';
import {GuidanceProgress} from '../components/guidance-progress/GuidanceProgress';
import {getEmailGuidanceItems} from './helper/email.helper';

interface IEmailStepController {
    sendEmail(): Promise<void>;
    readonly redirectedToStore: boolean;
}

export class EmailStepController extends InviteGenericStep implements IEmailStepController {
    private _flowManager: IInviteFlowManager;

    constructor(
        flowManager: IInviteFlowManager,
        mainLayoutController: IMainLayoutController,
        domUtilsService: IDomUtilsService
    ) {
        const params: InviteStepParams = {
            stepType: InviteStepsEnum.Email,
            content: () => <EmailStep ctrl={this} />,
            toolbar: () => <React.Fragment />
        };

        super(flowManager, mainLayoutController, domUtilsService, params);

        this._flowManager = flowManager;
    }

    get redirectedToStore(): boolean {
        return this._flowManager.inviteState.redirectedToStore;
    }

    sendEmail() {
        return this._flowManager.sendEmail();
    }

    isEmailSent(): boolean {
        return this._flowManager.isEmailSent;
    }
}

@observer
export class EmailStep extends TechseeBaseComponent<InviteStepProps & {ctrl: EmailStepController}> {
    componentDidMount() {
        this.props.ctrl.setPagePlacement('email-sending-step');

        // Only send email if not already sent (same pattern as SMS)
        if (!this.props.ctrl.isEmailSent()) {
            this.props.ctrl.sendEmail();
        }
    }

    renderInternal() {
        const {translate, flowManager, redirectedToStore: isClientRedirectedToStore} = this.props.ctrl;
        const {meetingMode} = flowManager.inviteState.sessionInfo;
        const {isNativeFullAssistSelected: isNativeFullAssist} = flowManager.inviteMethodController;
        const tabsList: ITab[] = [];

        tabsList.push({
            title: 'Android',
            content: emailTabs(InviteDeviceType.android, isClientRedirectedToStore)
        });
        tabsList.push({title: 'iOS', content: emailTabs(InviteDeviceType.ios, isClientRedirectedToStore)});

        return (
            <React.Fragment>
                <InviteStepsLayout
                    className={'email-step'}
                    h1Text={translate(
                        isClientRedirectedToStore ? 'REACT.EMAIL.SEND_EMAIL_INVITE' : 'REACT.EMAIL.RECEIVED_EMAIL'
                    )}
                    h2Text={translate(
                        isClientRedirectedToStore ? 'REACT.EMAIL.VIEW.CONTENT_INVITE' : 'REACT.EMAIL.VIEW.CONTENT'
                    )}>
                    <SimpleTabs
                        key={this.props.ctrl.getSelectedDeviceTabIndex}
                        tabsList={tabsList}
                        uid={'email-step-tabs'}
                        initialTabIndex={this.props.ctrl.getSelectedDeviceTabIndex}
                        onChange={this.props.ctrl.onDeviceTabChange}
                    />
                </InviteStepsLayout>
            </React.Fragment>
        );

        function emailTabs(device: InviteDeviceType, isClientRedirectedToStore: boolean) {
            const guidanceItems = getEmailGuidanceItems(
                device,
                isClientRedirectedToStore,
                translate,
                meetingMode,
                isNativeFullAssist
            );

            return () => <GuidanceProgress progressList={[]} guidanceItems={guidanceItems} />;
        }
    }
}
